import { useEffect, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { SplitButton } from '../../../components/general/SplitButton/SplitButton.tsx';
import { cn } from '../../../lib/utils.ts';
import { SAVE_MODE_OPTIONS, type SaveMode, type SaveModePreference } from '../hooks/useSaveModePreference.ts';

export type SaveState = 'idle' | 'saving' | 'saved';

export type SaveBlocker = 'booked' | 'checking' | 'invalid' | null;

export type EditorActionsProps = {
    saveState: SaveState;
    saveBlocker: SaveBlocker;
    isDirty: boolean;
    onSave: (mode: SaveMode) => void;
    saveMode: SaveModePreference;
    saveNudge?: number;
};

const CALLOUT_MS = 2000;

function statusText(saveState: SaveState, saveBlocker: SaveBlocker, isDirty: boolean): string {
    if (saveState === 'saving') return 'Opslaan…';
    if (saveState === 'saved') return 'Opgeslagen';
    switch (saveBlocker) {
        case 'booked':
            return 'Dit document is geboekt en kan niet worden aangepast.';
        case 'checking':
            return 'Controleren…';
        case 'invalid':
            return 'Los de gemarkeerde punten op om op te slaan.';
        default:
            return isDirty ? 'Wijzigingen worden pas opgeslagen na Opslaan.' : 'Geen openstaande wijzigingen.';
    }
}

function SaveButtonContent({ saveState, label }: { saveState: SaveState; label: string }) {
    const saved = saveState === 'saved';
    return (
        <span className="inline-flex items-center gap-2">
            {saved && <CheckIcon className="h-4 w-4" aria-hidden="true" />}
            {saveState === 'saving' && (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
            )}
            <span>{saved ? 'Opgeslagen' : label}</span>
        </span>
    );
}

export function EditorActions({ saveState, saveBlocker, isDirty, onSave, saveMode, saveNudge = 0 }: EditorActionsProps) {
    const disabled = saveBlocker !== null || saveState === 'saving';
    const saved = saveState === 'saved';
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [dismissedNudge, setDismissedNudge] = useState(saveNudge);
    const calloutVisible = dismissedNudge !== saveNudge;

    useEffect(() => {
        if (saveNudge === dismissedNudge) return;
        buttonRef.current?.focus();
        const dismiss = () => setDismissedNudge(saveNudge);
        const timer = window.setTimeout(dismiss, CALLOUT_MS);
        const frame = window.requestAnimationFrame(() => {
            window.addEventListener('keydown', dismiss, { once: true });
            window.addEventListener('pointerdown', dismiss, { once: true });
        });
        return () => {
            window.clearTimeout(timer);
            window.cancelAnimationFrame(frame);
            window.removeEventListener('keydown', dismiss);
            window.removeEventListener('pointerdown', dismiss);
        };
    }, [saveNudge, dismissedNudge]);

    return (
        <div className="flex items-center justify-between gap-4 border-t border-gray-200 bg-white px-4 py-2.5">
            <p className="text-[12px] text-gray-500" aria-live="polite">
                {statusText(saveState, saveBlocker, isDirty)}
            </p>
            <div className="relative">
                {calloutVisible && (
                    <div
                        role="status"
                        className="absolute bottom-full right-0 z-20 mb-2.5 whitespace-nowrap rounded-md bg-gray-900 px-3 py-1.5 text-[12px] font-medium text-white shadow-lg"
                    >
                        {disabled ? 'Los eerst de gemarkeerde punten op' : 'Sla eerst op om verder te gaan'}
                        {!disabled && <kbd className="ml-2 rounded bg-white/20 px-1.5 py-0.5 text-[10px]">Enter</kbd>}
                        <span aria-hidden="true" className="absolute right-6 top-full h-0 w-0 border-x-[6px] border-t-[6px] border-x-transparent border-t-gray-900" />
                    </div>
                )}
                {saveMode.showChoice ? (
                    <SplitButton
                        key={saveMode.storageKey}
                        storageKey={saveMode.storageKey}
                        options={SAVE_MODE_OPTIONS}
                        defaultOptionId="save"
                        selectedId={saveMode.mode}
                        onSelectedChange={saveMode.select}
                        disabled={disabled}
                        onClick={(mode) => onSave(mode as SaveMode)}
                        menuAnchor="top end"
                        size="compact"
                        menuButtonAriaLabel="Kies opslaanactie"
                        primaryRef={buttonRef}
                        primaryContent={<SaveButtonContent saveState={saveState} label={saveMode.label} />}
                    />
                ) : (
                    <button
                        ref={buttonRef}
                        type="button"
                        onClick={() => onSave(saveMode.mode)}
                        disabled={disabled}
                        aria-keyshortcuts="Control+Enter Meta+Enter"
                        className={cn(
                            'inline-flex h-9 items-center gap-2 rounded-md px-4 text-[13px] font-semibold text-white transition-colors',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                            saved ? 'bg-green-600' : 'bg-primary hover:bg-darkprimary',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                        )}
                    >
                        <SaveButtonContent saveState={saveState} label={saveMode.label} />
                    </button>
                )}
            </div>
        </div>
    );
}
